import type { CriterionContribution, DimensionScore, ScoreCard, ScoreDimension } from '@convometry/contracts';
import { AlertTriangle, ChevronRight, ShieldCheck } from 'lucide-react';
import * as React from 'react';
import { cn } from '../../lib/cn.js';
import { formatNumber, formatPercent } from '../../lib/format.js';
import { ProgressBar } from '../primitives/progress.js';

// ───────── ScoreCardView ─────────

/** Score band used for tone; text always accompanies color. Null = no data (never 0). */
export type ScoreTone = 'good' | 'fair' | 'poor' | 'none';

export function scoreTone(score: number | null | undefined): ScoreTone {
  if (score === null || score === undefined) return 'none';
  return score >= 80 ? 'good' : score >= 60 ? 'fair' : 'poor';
}

const TONE: Record<ScoreTone, { text: string; bar: string; label: string }> = {
  good: { text: 'text-success', bar: 'bg-success', label: 'Bien' },
  fair: { text: 'text-warning', bar: 'bg-warning', label: 'A mejorar' },
  poor: { text: 'text-danger', bar: 'bg-danger', label: 'Atención' },
  none: { text: 'text-text-muted', bar: 'bg-surface-subtle', label: 'Sin datos' },
};

export const formatScore = (v: number | null | undefined): string => (v === null || v === undefined ? '—' : formatNumber(v, { maximumFractionDigits: 0 }));

export interface ScoreCardViewProps {
  card: ScoreCard;
  /** Accessible name / heading of the overall score. */
  label?: string;
  /** Called when a dimension row is activated (e.g. open a drill-down). */
  onSelectDimension?: (dimension: ScoreDimension) => void;
  /** Called when a contribution ("¿Por qué?") is activated. */
  onSelectContribution?: (contribution: CriterionContribution, dimension: ScoreDimension) => void;
  /** Extra content under each contribution (e.g. example conversation / evidence links). */
  renderContribution?: (contribution: CriterionContribution, dimension: ScoreDimension) => React.ReactNode;
  /** Top contributions listed per dimension. */
  maxContributions?: number;
  /** Shows a "Sin calibrar" marker next to the overall score. */
  uncalibrated?: boolean;
  className?: string;
}

/** Overall score + six explained dimension scores (§22). Null scores render as "Sin datos", never as 0. */
export function ScoreCardView({ card, label = 'Puntaje general', onSelectDimension, onSelectContribution, renderContribution, maxContributions = 3, uncalibrated, className }: ScoreCardViewProps) {
  const overallTone = scoreTone(card.overall);
  return (
    <div className={cn('grid gap-4', className)}>
      <div className="flex flex-wrap items-end gap-x-3 gap-y-1">
        <div className="grid">
          <span className="text-helper font-medium text-text-secondary">{label}</span>
          <span className={cn('cm-tabular text-kpi font-semibold', card.overall === null ? 'text-text-muted' : 'text-text-primary')} data-testid="score-overall">
            {card.overall === null ? '—' : formatScore(card.overall)}
            {card.overall !== null ? <span className="text-body font-normal text-text-secondary"> / 100</span> : null}
          </span>
        </div>
        <span className={cn('pb-1.5 text-helper font-medium', TONE[overallTone].text)}>{card.overall === null ? 'Sin datos suficientes' : TONE[overallTone].label}</span>
        {uncalibrated ? (
          <span className="mb-1.5 inline-flex items-center gap-1 rounded-pill border border-warning/25 bg-warning-bg px-2 py-0.5 text-helper font-medium text-warning">
            <AlertTriangle aria-hidden className="size-3.5" /> Sin calibrar
          </span>
        ) : null}
      </div>
      <ul className="grid gap-3" aria-label="Puntajes por dimensión">
        {card.dimensions.map((d) => (
          <DimensionRow
            key={d.dimension}
            d={d}
            maxContributions={maxContributions}
            onSelectDimension={onSelectDimension}
            onSelectContribution={onSelectContribution}
            renderContribution={renderContribution}
          />
        ))}
      </ul>
    </div>
  );
}

function DimensionRow({
  d,
  maxContributions,
  onSelectDimension,
  onSelectContribution,
  renderContribution,
}: {
  d: DimensionScore;
  maxContributions: number;
  onSelectDimension?: ScoreCardViewProps['onSelectDimension'];
  onSelectContribution?: ScoreCardViewProps['onSelectContribution'];
  renderContribution?: ScoreCardViewProps['renderContribution'];
}) {
  const tone = scoreTone(d.score);
  const issues = d.contributions.filter((c) => c.score !== null && c.lostPoints > 0).slice(0, maxContributions);
  const title = onSelectDimension ? (
    <button
      type="button"
      onClick={() => onSelectDimension(d.dimension)}
      className="inline-flex items-center gap-1 rounded-control text-left text-body font-medium text-text-primary hover:text-brand-primary focus-visible:outline-2 focus-visible:outline-focus"
    >
      {d.label}
      <ChevronRight aria-hidden className="size-4 text-text-muted" />
    </button>
  ) : (
    <span className="text-body font-medium text-text-primary">{d.label}</span>
  );
  return (
    <li className="grid gap-1.5" data-dimension={d.dimension}>
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        {title}
        <span className="cm-tabular text-helper text-text-secondary">
          <span className={cn('text-body font-semibold', d.score === null ? 'text-text-muted' : TONE[tone].text)}>{d.score === null ? 'Sin datos' : formatScore(d.score)}</span>
          {d.share !== null ? ` · peso ${formatPercent(d.share, 0)}` : ''}
        </span>
      </div>
      <div className="h-2 w-full overflow-hidden rounded-pill bg-surface-subtle" aria-hidden>
        {d.score !== null ? <div className={cn('h-full rounded-pill', TONE[tone].bar)} style={{ width: `${Math.max(2, Math.min(100, d.score))}%` }} /> : null}
      </div>
      <p className="text-helper text-text-secondary">
        {d.observations === 0 ? 'Sin observaciones calificables' : `${formatNumber(d.observations)} observaciones`}
        {d.excluded ? ` · ${formatNumber(d.excluded)} pendientes o sin medir (no cuentan)` : ''}
      </p>
      {issues.length ? (
        <details className="group rounded-menu border border-border-subtle px-3 py-2">
          <summary className="cursor-pointer text-helper font-medium text-text-primary focus-visible:outline-2 focus-visible:outline-focus">¿Por qué?</summary>
          <ul className="mt-2 grid gap-2">
            {issues.map((c) => (
              <li key={c.criterionKey} className="grid gap-1">
                <div className="flex flex-wrap items-baseline justify-between gap-2">
                  {onSelectContribution ? (
                    <button
                      type="button"
                      onClick={() => onSelectContribution(c, d.dimension)}
                      className="rounded-control text-left text-helper font-medium text-brand-primary hover:underline focus-visible:outline-2 focus-visible:outline-focus"
                    >
                      {c.label}
                    </button>
                  ) : (
                    <span className="text-helper font-medium text-text-primary">{c.label}</span>
                  )}
                  <span className="cm-tabular text-helper text-text-secondary">−{formatNumber(c.lostPoints, { maximumFractionDigits: 1 })} pts</span>
                </div>
                <p className="cm-tabular text-helper text-text-secondary">
                  {formatNumber(c.failed)} no cumple · {formatNumber(c.partial)} parcial · {formatNumber(c.passed)} cumple
                </p>
                {renderContribution ? renderContribution(c, d.dimension) : null}
              </li>
            ))}
          </ul>
        </details>
      ) : null}
    </li>
  );
}

// ───────── CalibrationBanner ─────────

export interface CalibrationBannerProps {
  reviewed: number;
  target: number;
  ready: boolean;
  /** Optional action (e.g. link to calibration). */
  action?: React.ReactNode;
  className?: string;
}

/** Scores must be calibrated on manually reviewed conversations before comparing advisors (§22). */
export function CalibrationBanner({ reviewed, target, ready, action, className }: CalibrationBannerProps) {
  return (
    <div
      role="status"
      className={cn(
        'grid gap-2 rounded-card border px-4 py-3 sm:grid-cols-[minmax(0,1fr)_auto] sm:items-center',
        ready ? 'border-success/25 bg-success-bg' : 'border-warning/25 bg-warning-bg',
        className,
      )}
    >
      <div className="grid gap-2">
        <p className={cn('flex items-start gap-2 text-helper font-medium', ready ? 'text-success' : 'text-warning')}>
          {ready ? <ShieldCheck aria-hidden className="mt-0.5 size-4 shrink-0" /> : <AlertTriangle aria-hidden className="mt-0.5 size-4 shrink-0" />}
          {ready
            ? `Puntajes calibrados con ${formatNumber(reviewed)} conversaciones revisadas manualmente.`
            : 'Puntajes sin calibrar: no los uses para comparar asesores todavía.'}
        </p>
        {!ready ? (
          <ProgressBar
            className="max-w-sm"
            value={target > 0 ? (reviewed / target) * 100 : null}
            label="Conversaciones revisadas para calibración"
            valueText={`${formatNumber(reviewed)} de ${formatNumber(target)} conversaciones revisadas`}
          />
        ) : null}
      </div>
      {action ? <div className="flex shrink-0 gap-2">{action}</div> : null}
    </div>
  );
}
